<template>
  <div class="flex flex-col items-start justify-start gap-3 !h-[400px]">
    <Modal v-model:is-open="change_metric_color_modal" default-success-msg="Search Item Deleted">
      <template #form="{}">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Change Key Metric") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>
            {{ $store.state.metrics.filter((m) => m.metric_colors?.is_primary_metric_color)[0].name }}
            {{ $t("is already set as the key metric, do you want to replace this metric as the key metric instead?") }}
          </p>
        </div>
        <div class="flex items-center justify-center gap-2 mt-5 text-center">
          <button
            type="button"
            class="inline-flex cancel-btn"
            @click="
              () => {
                current_metric.metric_colors.is_primary_metric_color = false
                change_metric_color_modal = false
              }
            ">
            {{ $t("Cancel") }}
          </button>

          <button
            type="button"
            class="inline-flex items-center justify-center w-40 px-4 py-2 font-medium border border-transparent rounded-md primary-btn focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="change_metric_color_modal = false">
            {{ $t("Replace Key Metric") }}
          </button>
        </div>
      </template>
    </Modal>

    <div class="mb-5 dark:text-white">
      {{ $t("Add ranges to define colors") }}
    </div>
    <button class="primary-outline-btn btn w-fit bg-white" @click.prevent="addRange">
      <Plus class="w-4 h-4 mr-2" /> {{ $t("Add Range") }}
    </button>
    <div :class="{ 'mt-5': current_metric.metric_colors?.ranges?.length < 3 }" class="relative w-[80%]">
      <div class="mt-24 mb-10 relative w-full ml-[5%]">
        <Slider
          v-if="current_metric.metric_colors?.ranges?.length > 2 && show_slider && slider_handle_postions.length"
          v-model="slider_handle_postions"
          :lazy="true"
          :tooltips="false"
          @update="handlePercentage" />
      </div>

      <div
        v-if="current_metric.metric_colors?.ranges?.length > 1"
        :style="{
          backgroundColor: current_metric.metric_colors?.ranges[current_metric.metric_colors?.ranges?.length - 1]
            ? current_metric.metric_colors?.ranges[current_metric.metric_colors?.ranges?.length - 1][0]
            : 'red'
        }"
        :class="`w-8 h-8 ${current_metric.metric_colors?.ranges.length == 2 ? 'left-[100%]' : ''} rounded-full ${
          isScore ? '-right-14' : '-right-10'
        }  absolute -mt-[58px] z-20 border-[3px] shadow-md border-white`" />
      <div
        v-if="current_metric.metric_colors?.ranges?.length"
        :style="{ backgroundColor: current_metric.metric_colors?.ranges[0] ? current_metric.metric_colors?.ranges[0][0] : 'red' }"
        class="w-8 h-8 rounded-full left-5 absolute -mt-[58px] z-20 border-[3px] shadow-md border-white" />
      <div v-if="current_metric.metric_colors?.ranges?.length > 2">
        <div
          v-for="(dot, index) in slider_handle_postions.slice(0, slider_handle_postions.length - 1)"
          :key="index"
          :style="{
            left:
              current_metric.metric_colors?.ranges.length == 2
                ? `${isScore ? '104' : '100'}%`
                : `calc(${dot}% + ${isScore ? '15' : '10'}px)`,
            backgroundColor: current_metric.metric_colors?.ranges[index + 1] ? current_metric.metric_colors?.ranges[index + 1][0] : 'red'
          }"
          class="w-8 h-8 bg-red-500 rounded-full absolute -mt-[58px] z-20 pointer-events-none border-[3px] shadow-md border-white" />

        <div />
        <div
          v-for="(dot, index) in slider_handle_postions"
          :key="index"
          :style="{
            left: `calc(${!index ? 0 : slider_handle_postions[index - 1]}% + ${isScore ? '38' : '38'}px)`,
            width: `calc(${!index ? dot : dot - slider_handle_postions[index - 1]}% - ${
              slider_handle_postions.length - 1 == index ? '0' : '20'
            }px)`,
            backgroundColor: current_metric.metric_colors?.ranges[index] ? current_metric.metric_colors?.ranges[index][0] : null
          }"
          class="h-4 absolute -mt-[50px] z-10 group">
          <div class="ml-[40%] -mt-10">
            <ColorPickerList
              :transparent-icon="true"
              :large-icon="true"
              :open-to-bottom="true"
              class="relative"
              :current-item="current_metric.metric_colors?.ranges[index][0]"
              @update:currentItem="(e) => changeColor(index, e)" />
          </div>

          <div class="ml-[45%] -mt-9 pointer-events-none scale-125 opacity-0 group-hover:opacity-100 transition-all delay-600">
            <Pipette color="white" class="w-3 h-3 absolute ml-4 mt-1" />
            <div>
              <svg width="42"
                   height="26"
                   viewBox="0 0 42 26"
                   fill="none"
                   xmlns="http://www.w3.org/2000/svg">
                <mask id="path-1-inside-1_3040_2121" fill="white">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M10.9437 0C4.89965 0 0 4.89964 0 10.9437C0 16.9877 4.89964 21.8873 10.9437 21.8873H18.2243C18.27 22.0154 18.3299 22.142 18.405 22.2654L19.7077 24.4052C20.4869 25.6852 22.3451 25.6852 23.1243 24.4052L24.427 22.2654C24.5021 22.142 24.5621 22.0154 24.6077 21.8873H31.0563C37.1004 21.8873 42 16.9877 42 10.9437C42 4.89964 37.1004 0 31.0563 0H10.9437Z" />
                </mask>
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M10.9437 0C4.89965 0 0 4.89964 0 10.9437C0 16.9877 4.89964 21.8873 10.9437 21.8873H18.2243C18.27 22.0154 18.3299 22.142 18.405 22.2654L19.7077 24.4052C20.4869 25.6852 22.3451 25.6852 23.1243 24.4052L24.427 22.2654C24.5021 22.142 24.5621 22.0154 24.6077 21.8873H31.0563C37.1004 21.8873 42 16.9877 42 10.9437C42 4.89964 37.1004 0 31.0563 0H10.9437Z"
                  :fill="current_metric.metric_colors?.ranges[index][0]" />
                <path
                  d="M18.2243 21.8873L20.1081 21.2156L19.6345 19.8873H18.2243V21.8873ZM18.405 22.2654L20.1133 21.2254V21.2254L18.405 22.2654ZM19.7077 24.4052L21.416 23.3652L21.416 23.3652L19.7077 24.4052ZM23.1243 24.4052L24.8327 25.4452L24.8327 25.4452L23.1243 24.4052ZM24.427 22.2654L22.7187 21.2253L22.7187 21.2254L24.427 22.2654ZM24.6077 21.8873V19.8873H23.1976L22.7239 21.2156L24.6077 21.8873ZM2 10.9437C2 6.00421 6.00421 2 10.9437 2V-2C3.79508 -2 -2 3.79507 -2 10.9437H2ZM10.9437 19.8873C6.00421 19.8873 2 15.8831 2 10.9437H-2C-2 18.0922 3.79507 23.8873 10.9437 23.8873V19.8873ZM18.2243 19.8873H10.9437V23.8873H18.2243V19.8873ZM20.1133 21.2254C20.1087 21.2178 20.1077 21.2145 20.1081 21.2156L16.3405 22.5591C16.4322 22.8164 16.5511 23.0663 16.6967 23.3054L20.1133 21.2254ZM21.416 23.3652L20.1133 21.2254L16.6967 23.3054L17.9994 25.4452L21.416 23.3652ZM21.416 23.3652V23.3652L17.9994 25.4452C19.5578 28.0052 23.2742 28.0051 24.8327 25.4452L21.416 23.3652ZM22.7187 21.2254L21.416 23.3652L24.8327 25.4452L26.1354 23.3054L22.7187 21.2254ZM22.7239 21.2156C22.7243 21.2145 22.7233 21.2178 22.7187 21.2253L26.1354 23.3054C26.2809 23.0662 26.3998 22.8164 26.4916 22.5591L22.7239 21.2156ZM31.0563 19.8873H24.6077V23.8873H31.0563V19.8873ZM40 10.9437C40 15.8831 35.9958 19.8873 31.0563 19.8873V23.8873C38.2049 23.8873 44 18.0922 44 10.9437H40ZM31.0563 2C35.9958 2 40 6.00421 40 10.9437H44C44 3.79507 38.2049 -2 31.0563 -2V2ZM10.9437 2H31.0563V-2H10.9437V2Z"
                  :fill="current_metric.metric_colors?.ranges[index][0]"
                  mask="url(#path-1-inside-1_3040_2121)" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="current_metric.metric_colors?.ranges?.length > 1"
        :style="{
          left: current_metric.metric_colors?.ranges.length == 2 ? '100%' : `calc(100% + ${isScore ? '34' : '34'}px)`,
          width: `calc(80px)`,
          backgroundColor: current_metric.metric_colors?.ranges[current_metric.metric_colors?.ranges.length - 1]
            ? current_metric.metric_colors?.ranges[current_metric.metric_colors?.ranges.length - 1][0]
            : null
        }"
        class="h-4 absolute -mt-[50px] z-10 rounded-r-full group">
        <div class="ml-[40%] -mt-10">
          <ColorPickerList
            :transparent-icon="true"
            :large-icon="true"
            :open-to-bottom="true"
            class="relative"
            :current-item="current_metric.metric_colors?.ranges[current_metric.metric_colors?.ranges.length - 1][0]"
            @update:currentItem="(e) => changeColor(current_metric.metric_colors?.ranges.length - 1, e)" />
        </div>

        <div class="ml-[45%] -mt-9 pointer-events-none scale-125 opacity-0 group-hover:opacity-100 transition-all delay-600">
          <Pipette color="white" class="w-3 h-3 absolute ml-4 mt-1" />
          <div>
            <svg width="42"
                 height="26"
                 viewBox="0 0 42 26"
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <mask id="path-1-inside-1_3040_2121" fill="white">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M10.9437 0C4.89965 0 0 4.89964 0 10.9437C0 16.9877 4.89964 21.8873 10.9437 21.8873H18.2243C18.27 22.0154 18.3299 22.142 18.405 22.2654L19.7077 24.4052C20.4869 25.6852 22.3451 25.6852 23.1243 24.4052L24.427 22.2654C24.5021 22.142 24.5621 22.0154 24.6077 21.8873H31.0563C37.1004 21.8873 42 16.9877 42 10.9437C42 4.89964 37.1004 0 31.0563 0H10.9437Z" />
              </mask>
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M10.9437 0C4.89965 0 0 4.89964 0 10.9437C0 16.9877 4.89964 21.8873 10.9437 21.8873H18.2243C18.27 22.0154 18.3299 22.142 18.405 22.2654L19.7077 24.4052C20.4869 25.6852 22.3451 25.6852 23.1243 24.4052L24.427 22.2654C24.5021 22.142 24.5621 22.0154 24.6077 21.8873H31.0563C37.1004 21.8873 42 16.9877 42 10.9437C42 4.89964 37.1004 0 31.0563 0H10.9437Z"
                :fill="current_metric.metric_colors?.ranges[current_metric.metric_colors?.ranges.length - 1][0]" />
              <path
                d="M18.2243 21.8873L20.1081 21.2156L19.6345 19.8873H18.2243V21.8873ZM18.405 22.2654L20.1133 21.2254V21.2254L18.405 22.2654ZM19.7077 24.4052L21.416 23.3652L21.416 23.3652L19.7077 24.4052ZM23.1243 24.4052L24.8327 25.4452L24.8327 25.4452L23.1243 24.4052ZM24.427 22.2654L22.7187 21.2253L22.7187 21.2254L24.427 22.2654ZM24.6077 21.8873V19.8873H23.1976L22.7239 21.2156L24.6077 21.8873ZM2 10.9437C2 6.00421 6.00421 2 10.9437 2V-2C3.79508 -2 -2 3.79507 -2 10.9437H2ZM10.9437 19.8873C6.00421 19.8873 2 15.8831 2 10.9437H-2C-2 18.0922 3.79507 23.8873 10.9437 23.8873V19.8873ZM18.2243 19.8873H10.9437V23.8873H18.2243V19.8873ZM20.1133 21.2254C20.1087 21.2178 20.1077 21.2145 20.1081 21.2156L16.3405 22.5591C16.4322 22.8164 16.5511 23.0663 16.6967 23.3054L20.1133 21.2254ZM21.416 23.3652L20.1133 21.2254L16.6967 23.3054L17.9994 25.4452L21.416 23.3652ZM21.416 23.3652V23.3652L17.9994 25.4452C19.5578 28.0052 23.2742 28.0051 24.8327 25.4452L21.416 23.3652ZM22.7187 21.2254L21.416 23.3652L24.8327 25.4452L26.1354 23.3054L22.7187 21.2254ZM22.7239 21.2156C22.7243 21.2145 22.7233 21.2178 22.7187 21.2253L26.1354 23.3054C26.2809 23.0662 26.3998 22.8164 26.4916 22.5591L22.7239 21.2156ZM31.0563 19.8873H24.6077V23.8873H31.0563V19.8873ZM40 10.9437C40 15.8831 35.9958 19.8873 31.0563 19.8873V23.8873C38.2049 23.8873 44 18.0922 44 10.9437H40ZM31.0563 2C35.9958 2 40 6.00421 40 10.9437H44C44 3.79507 38.2049 -2 31.0563 -2V2ZM10.9437 2H31.0563V-2H10.9437V2Z"
                :fill="current_metric.metric_colors?.ranges[current_metric.metric_colors?.ranges.length - 1][0]"
                mask="url(#path-1-inside-1_3040_2121)" />
            </svg>
          </div>
        </div>
      </div>

      <div
        v-if="current_metric.metric_colors?.ranges?.length < 3 && current_metric.metric_colors?.ranges[0]"
        :style="{
          left: `34px`,
          width: `96%`,
          backgroundColor: current_metric.metric_colors?.ranges[0] ? current_metric.metric_colors?.ranges[0][0] : null
        }"
        class="h-4 absolute -mt-[50px] z-10 rounded-r-full group">
        <div class="ml-[40%] -mt-10">
          <ColorPickerList
            :transparent-icon="true"
            :large-icon="true"
            :open-to-bottom="true"
            class="relative"
            :current-item="current_metric.metric_colors?.ranges[0][0]"
            @update:currentItem="(e) => changeColor(0, e)" />
        </div>

        <div class="ml-[45%] -mt-9 pointer-events-none scale-125 opacity-0 group-hover:opacity-100 transition-all delay-600">
          <Pipette color="white" class="w-3 h-3 absolute ml-4 mt-1" />
          <div>
            <svg width="42"
                 height="26"
                 viewBox="0 0 42 26"
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <mask id="path-1-inside-1_3040_2121" fill="white">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M10.9437 0C4.89965 0 0 4.89964 0 10.9437C0 16.9877 4.89964 21.8873 10.9437 21.8873H18.2243C18.27 22.0154 18.3299 22.142 18.405 22.2654L19.7077 24.4052C20.4869 25.6852 22.3451 25.6852 23.1243 24.4052L24.427 22.2654C24.5021 22.142 24.5621 22.0154 24.6077 21.8873H31.0563C37.1004 21.8873 42 16.9877 42 10.9437C42 4.89964 37.1004 0 31.0563 0H10.9437Z" />
              </mask>
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M10.9437 0C4.89965 0 0 4.89964 0 10.9437C0 16.9877 4.89964 21.8873 10.9437 21.8873H18.2243C18.27 22.0154 18.3299 22.142 18.405 22.2654L19.7077 24.4052C20.4869 25.6852 22.3451 25.6852 23.1243 24.4052L24.427 22.2654C24.5021 22.142 24.5621 22.0154 24.6077 21.8873H31.0563C37.1004 21.8873 42 16.9877 42 10.9437C42 4.89964 37.1004 0 31.0563 0H10.9437Z"
                :fill="current_metric.metric_colors?.ranges[0][0]" />
              <path
                d="M18.2243 21.8873L20.1081 21.2156L19.6345 19.8873H18.2243V21.8873ZM18.405 22.2654L20.1133 21.2254V21.2254L18.405 22.2654ZM19.7077 24.4052L21.416 23.3652L21.416 23.3652L19.7077 24.4052ZM23.1243 24.4052L24.8327 25.4452L24.8327 25.4452L23.1243 24.4052ZM24.427 22.2654L22.7187 21.2253L22.7187 21.2254L24.427 22.2654ZM24.6077 21.8873V19.8873H23.1976L22.7239 21.2156L24.6077 21.8873ZM2 10.9437C2 6.00421 6.00421 2 10.9437 2V-2C3.79508 -2 -2 3.79507 -2 10.9437H2ZM10.9437 19.8873C6.00421 19.8873 2 15.8831 2 10.9437H-2C-2 18.0922 3.79507 23.8873 10.9437 23.8873V19.8873ZM18.2243 19.8873H10.9437V23.8873H18.2243V19.8873ZM20.1133 21.2254C20.1087 21.2178 20.1077 21.2145 20.1081 21.2156L16.3405 22.5591C16.4322 22.8164 16.5511 23.0663 16.6967 23.3054L20.1133 21.2254ZM21.416 23.3652L20.1133 21.2254L16.6967 23.3054L17.9994 25.4452L21.416 23.3652ZM21.416 23.3652V23.3652L17.9994 25.4452C19.5578 28.0052 23.2742 28.0051 24.8327 25.4452L21.416 23.3652ZM22.7187 21.2254L21.416 23.3652L24.8327 25.4452L26.1354 23.3054L22.7187 21.2254ZM22.7239 21.2156C22.7243 21.2145 22.7233 21.2178 22.7187 21.2253L26.1354 23.3054C26.2809 23.0662 26.3998 22.8164 26.4916 22.5591L22.7239 21.2156ZM31.0563 19.8873H24.6077V23.8873H31.0563V19.8873ZM40 10.9437C40 15.8831 35.9958 19.8873 31.0563 19.8873V23.8873C38.2049 23.8873 44 18.0922 44 10.9437H40ZM31.0563 2C35.9958 2 40 6.00421 40 10.9437H44C44 3.79507 38.2049 -2 31.0563 -2V2ZM10.9437 2H31.0563V-2H10.9437V2Z"
                :fill="current_metric.metric_colors?.ranges[0][0]"
                mask="url(#path-1-inside-1_3040_2121)" />
            </svg>
          </div>
        </div>
      </div>

      <div class="flex -mt-8">
        <div
          v-for="(color, i) in colors_array"
          :key="i"
          :style="{
            width: '60px',
            marginLeft: `${
              i
                ? i == colors_array.length - 1
                  ? `calc(${percentages_margin_left_array[i]}% - ${isScore ? '0' : '15'}px)`
                  : `calc(${percentages_margin_left_array[i]}% - ${isScore ? '0' : '8'}px)`
                : 0
            }${i == colors_array.length - 1 ? '' : ''}`
          }"
          :class="[`dark:text-white absolute  -mt-[80px] text-center`]">
          <svg width="71"
               height="45"
               viewBox="0 0 71 45"
               fill="none"
               xmlns="http://www.w3.org/2000/svg"
               class="absolute z-10 scale-125">
            <mask id="path-1-inside-1_3040_1994" fill="white">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M18.5 0C8.28273 0 0 8.28273 0 18.5C0 28.7173 8.28273 37 18.5 37H30.7239L34.4946 43.1939C35.2739 44.4739 37.1321 44.4739 37.9113 43.1939L41.6821 37H52.5C62.7173 37 71 28.7173 71 18.5C71 8.28273 62.7173 0 52.5 0H18.5Z" />
            </mask>
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M18.5 0C8.28273 0 0 8.28273 0 18.5C0 28.7173 8.28273 37 18.5 37H30.7239L34.4946 43.1939C35.2739 44.4739 37.1321 44.4739 37.9113 43.1939L41.6821 37H52.5C62.7173 37 71 28.7173 71 18.5C71 8.28273 62.7173 0 52.5 0H18.5Z"
              fill="white" />
            <path
              d="M30.7239 37L32.4322 35.96L31.8478 35H30.7239V37ZM34.4946 43.1939L32.7863 44.2339L34.4946 43.1939ZM37.9113 43.1939L39.6196 44.2339L37.9113 43.1939ZM41.6821 37V35H40.5582L39.9738 35.96L41.6821 37ZM2 18.5C2 9.3873 9.3873 2 18.5 2V-2C7.17816 -2 -2 7.17816 -2 18.5H2ZM18.5 35C9.3873 35 2 27.6127 2 18.5H-2C-2 29.8218 7.17816 39 18.5 39V35ZM30.7239 35H18.5V39H30.7239V35ZM36.203 42.1539L32.4322 35.96L29.0155 38.04L32.7863 44.2339L36.203 42.1539ZM36.203 42.1539H36.203L32.7863 44.2339C34.3448 46.7939 38.0611 46.7939 39.6196 44.2339L36.203 42.1539ZM39.9738 35.96L36.203 42.1539L39.6196 44.2339L43.3904 38.04L39.9738 35.96ZM52.5 35H41.6821V39H52.5V35ZM69 18.5C69 27.6127 61.6127 35 52.5 35V39C63.8218 39 73 29.8218 73 18.5H69ZM52.5 2C61.6127 2 69 9.3873 69 18.5H73C73 7.17816 63.8218 -2 52.5 -2V2ZM18.5 2H52.5V-2H18.5V2Z"
              :fill="current_metric.metric_colors?.ranges[i][0]"
              mask="url(#path-1-inside-1_3040_1994)" />
          </svg>

          <div class="flex group relative z-20">
            <input
              :disabled="!i"
              :value="margin_left_array[i]"
              name="percentage"
              type="number"
              class="appearance-none focus:ring-0 block w-[76px] text-center ml-0 p-2 border-0 bg-transparent border-light-divider-blue dark:border-dark-divider-border rounded-md dark:bg-dark-screen-background text-grey-light dark:text-white text-[15px] focus:outline-none focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue sm:text-sm"
              @input="(e) => handleInputPercentage(e, i)">
            <div
              v-if="margin_left_array.length > 1 ? (!i ? false : true) : true"
              class="absolute mt-2 -right-12 z-10 opacity-0 group-hover:opacity-100 transition-all"
              @click="deleteRange(i)">
              <Trash2 class="w-5 h-5 text-common-grey hover:text-red-500 transition-all cursor-pointer" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="overlapping_error" class="absolute mt-2 text-red-error/75">
      {{ $t("Values are overlapping, please modify and submit again") }}
    </div>
    <div class="flex space-x-6">
      <div class="justify-end pb-4 pl-2 my-2 overflow-visible text-xs font-medium">
        <SwitchGroup as="div" class="flex items-end justify-between" @click="handleKeyMetricChange">
          <span class="flex flex-col flex-grow">
            <SwitchLabel as="span" class="text-sm font-medium text-gray-900 dark:text-white" passive>{{
              $t("Primary object color")
            }}</SwitchLabel>
          </span>
          <div class="flex items-center pl-2 space-x-1">
            <Switch
              v-model="current_metric.metric_colors.is_primary_metric_color"
              :class="[
                current_metric.metric_colors.is_primary_metric_color ? 'bg-blue' : 'bg-gray-200 dark:bg-gray-500',
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
              ]">
              <span
                aria-hidden="true"
                :class="[
                  current_metric.metric_colors.is_primary_metric_color && $store.state.portfolio.ui_options?.language == 'ar'
                    ? '-translate-x-5'
                    : current_metric.metric_colors.is_primary_metric_color
                      ? 'translate-x-5'
                      : 'translate-x-0',
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                ]" />
            </Switch>
          </div>
        </SwitchGroup>
      </div>

      <div class="justify-end pb-4 pl-2 my-2 overflow-visible text-xs font-medium">
        <SwitchGroup as="div" class="flex items-end justify-between">
          <span class="flex flex-col flex-grow">
            <SwitchLabel as="span" class="text-sm font-medium text-gray-900 dark:text-white" passive>{{
              $t("Is decrease positive?")
            }}</SwitchLabel>
          </span>
          <div class="flex items-center pl-2 space-x-1">
            <Switch
              v-model="current_metric.metric_colors.is_decrease_positive"
              :class="[
                current_metric.metric_colors.is_decrease_positive ? 'bg-blue' : 'bg-gray-200 dark:bg-gray-500',
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
              ]">
              <span
                aria-hidden="true"
                :class="[
                  current_metric.metric_colors.is_decrease_positive && $store.state.portfolio.ui_options?.language == 'ar'
                    ? '-translate-x-5'
                    : current_metric.metric_colors.is_decrease_positive
                      ? 'translate-x-5'
                      : 'translate-x-0',
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                ]" />
            </Switch>
          </div>
        </SwitchGroup>
      </div>
    </div>

    <div v-if="current_metric.metric_colors?.metric_color_display_option" class="flex mb-10">
      <!-- <div class="mt-2 mr-4 dark:text-white">
        Display color as:
      </div> -->

      <SelectInput
        v-model="current_metric.metric_colors.metric_color_display_option"
        name="tag"
        :allow-null="false"
        :items="metric_color_display_options"
        label-attr="label"
        value-attr="value"
        :placeholder="$t('Display color as')"
        class="w-56"
        :label="$t('Display color as')"
        input-classes="appearance-none text-left block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm" />

      <div class="ml-4">
        <div v-if="current_metric.metric_colors.metric_color_display_option == 'background'" class="flex items-center mb-2 dark:text-white">
          {{ $t("Background Opacity") }}:
          <TextInput
            :validate-on-input="true"
            label=""
            :placeholder="current_metric.metric_colors.metric_color_display_opacity"
            :classes="'ml-2 w-[45px]'"
            input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
            name="metric_color_display_opacity"
            :value="current_metric.metric_colors.metric_color_display_opacity"
            type="number"
            @update:modelValue="
              (e) => {
                current_metric.metric_colors.metric_color_display_opacity = e
              }
            " />
        </div>
        <Slider
          v-if="current_metric.metric_colors.metric_color_display_option == 'background'"
          v-model="current_metric.metric_colors.metric_color_display_opacity"
          :format="
            (value) => {
              return `${Math.round(value)}%`
            }
          "
          class="w-[200px]"
          :classes="{ base: 'w-full h-full relative z-1 bg-gray-300 rounded dark:bg-gray-600' }"
          tooltip-position="bottom" />
      </div>
      <div class="mt-2 ml-8 dark:text-white">
        Preview:
      </div>
      <div v-for="(range, i) in current_metric.metric_colors.ranges" :key="range" class="flex items-center">
        <span
          v-if="current_metric.metric_colors.metric_color_display_option == 'dot'"
          :style="{ background: range[0] }"
          class="flex-shrink-0 inline-block w-3 h-3 ml-2 mr-2 rounded-full opacity-90" />
        <div
          :style="{
            color: current_metric.metric_colors.metric_color_display_option == 'text' ? range[0] : '',
            background:
              current_metric.metric_colors.metric_color_display_option == 'background'
                ? `rgba(${hexToRgb(range[0]).r}, ${hexToRgb(range[0]).g}, ${hexToRgb(range[0]).b}, ${
                  current_metric.metric_colors.metric_color_display_opacity / 100
                })`
                : ''
          }"
          :class="[`dark:text-white p-2 ${current_metric.metric_colors.metric_color_display_option != 'dot' ? 'ml-2' : ''}`]">
          Value {{ i + 1 }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { utils } from "@/store/helpers"
import { SwitchLabel, SwitchGroup, Switch } from "@headlessui/vue"
import Slider from "@vueform/slider"
import TextInput from "@/components/shared/TextInput.vue"
import Modal from "@/components/shared/Modal.vue"
import ColorPickerList from "@/components/shared/ColorPickerList.vue"
import { Pipette, Plus, Trash2 } from "lucide-vue-next"
import SelectInput from "@/components/shared/SelectInput.vue"

export default {
  components: {
    SelectInput,
    Pipette,
    Trash2,
    SwitchLabel,
    SwitchGroup,
    Switch,
    Slider,
    TextInput,
    Modal,
    ColorPickerList,
    Plus
  },
  props: {
    metricLevels: null,
    isScore: {
      type: Boolean,
      default: false
    },
    metric: {
      type: Object,
      required: true
    }
  },
  emits: ["update"],
  data() {
    return {
      metric_color_display_options: [
        { value: "dot", label: this.$t("Dot") },
        { value: "background", label: this.$t("Background Color") },
        { value: "text", label: this.$t("Text Color") },
        { value: "mixed", label: this.$t("Mixed") }
      ],
      colors_array: [],
      input_timeout: null,
      // slider_handle_postions: [20,40, 100],
      slider_handle_postions: [],
      current_metric: null,
      overlapping_error: null,
      change_metric_color_modal: false,
      margin_left_array: [],
      show_slider: true,
      // margin_left_array:[0, 20000, 40000, 100000],
      // percentage_slider: { options: { connect: [true, true, true, true] } },

      prevent_auto_update: false,

      color_palettes: [
        { css_hex: "bg-[#9B2948]", hex: "#9B2948" },
        { css_hex: "bg-[#FF7251]", hex: "#FF7251" },
        { css_hex: "bg-[#FFCA7B]", hex: "#FFCA7B" },
        { css_hex: "bg-[#FFCD74]", hex: "#FFCD74" },
        { css_hex: "bg-[#FFEDBF]", hex: "#FFEDBF" },
        { css_hex: "bg-[#FF7C7C]", hex: "#FF7C7C" },
        { css_hex: "bg-[#FFB680]", hex: "#FFB680" },
        { css_hex: "bg-[#FFEB88]", hex: "#FFEB88" },
        { css_hex: "bg-[#84FF8D]", hex: "#84FF8D" },
        { css_hex: "bg-[#9AFAFF]", hex: "#9AFAFF" },
        { css_hex: "bg-[#B2D8D8]", hex: "#B2D8D8" },
        { css_hex: "bg-[#66B2B2]", hex: "#66B2B2" },
        { css_hex: "bg-[#008080]", hex: "#008080" },
        { css_hex: "bg-[#006666]", hex: "#006666" },
        { css_hex: "bg-[#004C4C]", hex: "#004C4C" },
        { css_hex: "bg-[#FFC100]", hex: "#FFC100" },
        { css_hex: "bg-[#FF9A00]", hex: "#FF9A00" },
        { css_hex: "bg-[#FF7400]", hex: "#FF7400" },
        { css_hex: "bg-[#FF4D00]", hex: "#FF4D00" },
        { css_hex: "bg-[#FF0000]", hex: "#FF0000" }
      ]
    }
  },
  computed: {
    percentages_margin_left_array() {
      const furtheset = this.margin_left_array[this.margin_left_array.length - 1]
      const arr_calc = [0]
      this.margin_left_array.forEach((v, i) => {
        if (i) {
          const percentage_calc = (v / furtheset) * 100
          arr_calc.push(percentage_calc)
        }
      })

      return arr_calc
    }
  },
  watch: {
    metricLevels() {
      if (this.colors_array.length < this.metricLevels) {
        this.addRange()
      }
    },
    current_metric: {
      deep: true,
      handler() {
        if (this.checkForOverlappingRanges()) {
          this.$emit("update", this.current_metric)
        }
      }
    }
  },
  created() {
    this.current_metric = utils.clone(this.metric)

    if ((this.metric?.id == null || this.metric?.id == undefined) && !this.current_metric.metric_colors.ranges?.length) {
      this.current_metric["metric_colors"] = {
        ranges: [],
        is_primary_metric_color: false,
        is_decrease_positive: false,
        metric_color_display_option: "dot"
      }
    }

    if (this.current_metric.metric_colors.ranges?.length) {
      this.current_metric["metric_colors"] = this.metric.metric_colors
      const max_value = this.metric.metric_colors.ranges[this.metric.metric_colors.ranges.length - 1][1]
      this.metric.metric_colors.ranges.forEach((range, index) => {
        this.colors_array.push(index)
        this.margin_left_array.push(range[1])
        if (index) {
          this.slider_handle_postions.push((range[1] / max_value) * 100)
        }
      })
    }
  },
  methods: {
    hexToRgb(hex) {
      // Remove the hash at the start if it's there
      hex = hex.replace(/^#/, '')
      
      // Parse r, g, b values from hex
      const bigint = parseInt(hex, 16)
      const r = (bigint >> 16) & 255
      const g = (bigint >> 8) & 255
      const b = bigint & 255
      
      return { r, g, b }
    },
    handleInputPercentage(e, i) {
      clearTimeout(this.input_timeout)
      this.prevent_auto_update = true

      const backup_value = this.margin_left_array[i]

      this.input_timeout = setTimeout(() => {
        if (
          (this.margin_left_array[i - 1] && e.target.value < this.margin_left_array[i - 1]) ||
          (this.margin_left_array[i + 1] && e.target.value > this.margin_left_array[i + 1])
        ) {
          this.margin_left_array[i] = backup_value
          this.show_slider = false
          setTimeout(() => {
            this.show_slider = true
          }, 100)
        } else {
          this.prevent_auto_update = false
          this.margin_left_array[i] = parseInt(e.target.value)
          setTimeout(() => {
            this.slider_handle_postions[i - 1] = this.$utils.clone(this.percentages_margin_left_array[i])

            if (i == this.margin_left_array.length - 1) {
              this.handlePercentage(this.slider_handle_postions)
            }
          }, 300)
        }
      }, 850)
    },
    /** Sets  */
    handlePercentage(arr) {
      if (!this.prevent_auto_update) {
        const margin_left_array_calc = []
        margin_left_array_calc.push(0)
        arr.forEach((v) => {
          const furtheset = this.margin_left_array[this.margin_left_array.length - 1]
          const value_calc = (v / 100) * furtheset
          margin_left_array_calc.push(value_calc)
        })

        this.margin_left_array = margin_left_array_calc
      }

      // this.updateRanges()
    },

    handleKeyMetricChange() {
      if (this.current_metric.metric_colors?.is_primary_metric_color) {
        if (this.$store.state.metrics.filter((m) => m.metric_colors?.is_primary_metric_color)[0]) {
          this.change_metric_color_modal = true
        }
      }
    },

    addRange() {
      if (!this.current_metric.metric_colors.ranges?.length) {
        this.current_metric.metric_colors = {
          ranges: [["#9B2948", 0, this.isScore ? 2 : 25000]],
          is_primary_metric_color: false,
          metric_color_display_option: "dot",
          metric_color_display_opacity: 15
        }

        this.colors_array.push(this.colors_array.length)
        this.margin_left_array = [0]
      } else {
        let last = utils.clone(this.current_metric.metric_colors?.ranges[this.current_metric.metric_colors.ranges.length - 1])
        if (this.current_metric.metric_colors.ranges[this.current_metric.metric_colors.ranges.length - 1][2] == null) {
          this.current_metric.metric_colors.ranges[this.current_metric.metric_colors.ranges.length - 1][2] =
            this.current_metric.metric_colors.ranges[this.current_metric.metric_colors.ranges.length - 1][1] + (this.isScore ? 1 : 25000)
          last = utils.clone(this.current_metric.metric_colors.ranges[this.current_metric.metric_colors.ranges.length - 1])
        }
        last[1] = utils.clone(last[2])
        last[2] = null
        last[0] = this.color_palettes[parseInt(Math.random(0, 10) * 20)].hex
        this.current_metric.metric_colors.ranges.push(last)

        this.colors_array.push(this.colors_array.length)
        this.margin_left_array.push(last[1])
        if (!this.slider_handle_postions.length) {
          this.slider_handle_postions.push(100)
        } else {
          const furthest = this.margin_left_array[this.margin_left_array.length - 1]
          const arr_calc = []
          this.margin_left_array.forEach((v, i) => {
            if (i) {
              arr_calc.push((v / furthest) * 100)
            }
          })
          this.show_slider = false
          this.slider_handle_postions = arr_calc
          setTimeout(() => {
            this.show_slider = true
          }, 100)
        }

        // this.margin_left_array.push( last[1])
        // this.percentage_slider.options.connect.push(true)
        // this.slider_handle_postions.push(75)
        // this.handlePercentage( this.slider_handle_postions)
      }
    },
    checkForOverlappingRanges() {
      this.overlapping_error = false
      let ranges = null
      if (this.current_metric.metric_colors.ranges) {
        ranges = [...this.current_metric.metric_colors.ranges]
      } else {
        ranges = []
      }
      ranges.forEach((range, i) => {
        if (i + 1 < ranges.length && range[1] > range[2]) {
          this.overlapping_error = true
        }
        if (ranges[i + 1] && range[2] > ranges[i + 1][1]) {
          this.overlapping_error = true
        }
      })
      return !this.overlapping_error
    },
    changeColor(i, color) {
      this.current_metric.metric_colors.ranges[i][0] = color
    },
    deleteRange(i) {
      if (i && i < this.current_metric.metric_colors.ranges.length - 1) {
        this.current_metric.metric_colors.ranges[i - 1][2] = this.current_metric.metric_colors.ranges[i][2]
      }

      this.current_metric.metric_colors.ranges.splice(i, 1)
      this.colors_array.splice(this.colors_array.length - 1, 1)
      this.margin_left_array.splice(i, 1)

      if (this.slider_handle_postions.length == 1) {
        this.slider_handle_postions = []
      } else if (this.slider_handle_postions.length > 1) {
        const furthest = this.margin_left_array[this.margin_left_array.length - 1]
        const arr_calc = []
        this.margin_left_array.forEach((v, i) => {
          if (i) {
            arr_calc.push((v / furthest) * 100)
          }
        })
        this.show_slider = false
        this.slider_handle_postions = arr_calc
        setTimeout(() => {
          this.show_slider = true
        }, 100)
      }
    }
    // changeStartRange(e, i) {
    //   this.$utils.debounce(() => {
    //     this.current_metric.metric_colors.ranges.splice(
    //       i,
    //       1,
    //       this.$utils.clone([
    //         this.current_metric.metric_colors.ranges[i][0],
    //         parseInt(e.target.value),
    //         this.current_metric.metric_colors.ranges[i][2]
    //       ])
    //     )
    //   }, 800)
    // },

    // changeEndRange(e, i) {
    //   this.$utils.debounce(() => {
    //     this.current_metric.metric_colors.ranges.splice(
    //       i,
    //       1,
    //       this.$utils.clone([
    //         this.current_metric.metric_colors.ranges[i][0],
    //         this.current_metric.metric_colors.ranges[i][1],
    //         e.target.value
    //       ])
    //     )
    //   }, 800)
    // }
  }
}
</script>
